import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { OnboardingPropsBeta } from "models";
import {
    UpdateAppInput,
    useGetAppOverviewQuery,
    useUpdateApplicationMutation
} from "generated/graphql";
import { subDays } from "date-fns";
import { wrapUrqlMutation } from "utils/graphql";

import { OnboardingProps } from "../models";
import { SendLeadsStep, SendLeadsStepProps } from "../components/SendLeadsStep";

export interface SendLeadsStepContainerProps extends Omit<SendLeadsStepProps, 'onNext'> {
    onNext(app: Pick<OnboardingProps, "leadsContact" | "beta">): void;
}

export const SendLeadsStepContainer: FC<SendLeadsStepContainerProps> = props => {

    const { onNext: onNextStep, onUpgrade } = props;
    let { app } = props;
    const { appId } = app;

    const [loading, setLoading] = useState<boolean>();
    const [loadingMessage, setLoadingMessage] = useState<string>();
    const [error, setError] = useState<{ error: string, message: string }>();
    const [disabled, setDisabled] = useState<boolean>();

    const now = useMemo(() => new Date(), []);
    const [start,] = useState<Date>(subDays(now, 1));
    const [end,] = useState<Date>(now);

    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
        onErrorMessage: false
    });

    const [getAppOverview] = useGetAppOverviewQuery({
        pause: !appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId,
            end,
            start,
        },
    });

    const { data, error: appError, fetching } = getAppOverview;

    useEffect(() => {
        if (appError) {
            setError({ error: appError.message, message: 'We had trouble retrieving your app.' });
            setDisabled(true);
        } else {
            setError(undefined);
        }
    }, [appError])

    useEffect(() => {
        if (fetching) {
            setLoadingMessage("Preparing integrations")
        } else {
            setLoadingMessage("");
        }
    }, [fetching]);

    const leadsContact = data?.app?.leadsContact;
    if (leadsContact) {
        app.leadsContact = leadsContact;
        app = {
            ...app,
            leadsContact
        }
    }

    // opportunity alerts?

    const handleSave = useCallback(async (newApp: Pick<OnboardingProps, "leadsContact" | "beta">) => {
        // Save?

        const updatedApp: OnboardingProps = {
            ...app,
        };

        const updateApp: UpdateAppInput = {};

        if (newApp.leadsContact) {
            updatedApp.leadsContact = newApp.leadsContact;
            updateApp.leadsContact = newApp.leadsContact;
        }

        if (newApp.beta) {
            const beta: OnboardingPropsBeta = {
                ...app.beta,
                onboarding: {
                    ...app.beta.onboarding,
                    leadsIntegration: newApp.beta.onboarding.leadsIntegration,
                    lastStep: "integrations"
                }
            };

            updateApp.beta = beta;
            updatedApp.beta = beta;
        }

        if (updateApp.leadsContact || updateApp.beta) {
            setLoading(true);
            setLoadingMessage("Updating integrations...");
            await updateApplication({
                appId,
                app: updateApp,
            });
            setLoadingMessage("Updated. 👍");
        }

        onNextStep(updatedApp);
    }, [onNextStep, app, updateApplication, appId]);

    return (
        <SendLeadsStep
            {...props}
            loading={fetching || loading}
            loadingMessage={loadingMessage}
            error={error}
            disabled={disabled}
            app={app}
            onNext={handleSave}
            onUpgrade={onUpgrade}
        />
    );
}